import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminErrorBanner,
  AdminMetricCard,
  AdminPagination,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTable,
  AdminTd,
  AdminTr,
} from "@/components/admin";
import {
  fetchSoundModelLabels,
  fetchSoundModelMappingRules,
} from "@/lib/api";
import type { AdminStatusTone } from "@/lib/adminCodeDisplay";
import { formatDateTime } from "@/lib/format";
import type {
  SoundModelLabel,
  SoundModelMappingRule,
  SoundModelMappingStatus,
} from "@/types";

const MAPPING_STATUS_OPTIONS: Array<{ value: SoundModelMappingStatus | "ALL"; label: string }> = [
  { value: "ALL", label: "전체" },
  { value: "CANDIDATE", label: "후보" },
  { value: "APPLIED", label: "적용" },
  { value: "REJECTED", label: "반려" },
  { value: "CONFLICT", label: "충돌" },
];
const PAGE_SIZE = 20;
const LABEL_PAGE_SIZE = 20;


const statusTone = (status: string): AdminStatusTone => {
  if (status === "APPLIED") return "success";
  if (status === "CONFLICT") return "warning";
  if (status === "REJECTED") return "danger";
  return "neutral";
};

const statusLabel = (status: string) => MAPPING_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;

const labelText = (label: SoundModelLabel | null | undefined) => {
  if (!label) return "라벨 미지정";
  return `${label.labelCode} · ${label.modelLabelKo || label.modelLabel}`;
};

const SoundModelMappingRulesPage = () => {
  const [rules, setRules] = useState<SoundModelMappingRule[]>([]);
  const [labels, setLabels] = useState<SoundModelLabel[]>([]);
  const [activeStatus, setActiveStatus] = useState<SoundModelMappingStatus | "ALL">("ALL");
  const [keyword, setKeyword] = useState("");
  const [labelKeyword, setLabelKeyword] = useState("");
  const [labelUseYn, setLabelUseYn] = useState<"ALL" | "Y" | "N">("ALL");
  const [labelPage, setLabelPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadRules = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const nextRules = await fetchSoundModelMappingRules({
        mappingStatus: activeStatus === "ALL" ? undefined : activeStatus,
      });
      setRules(nextRules);
    } catch (error) {
      setRules([]);
      setErrorMessage(error instanceof Error ? error.message : "소리 매핑 룰 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [activeStatus]);

  const loadReferences = useCallback(async () => {
    try {
      setLabels(await fetchSoundModelLabels());
    } catch (error) {
      setLabels([]);
      setErrorMessage(error instanceof Error ? error.message : "소리 매핑 기준 데이터를 불러오지 못했습니다.");
    }
  }, []);

  useEffect(() => {
    void loadReferences();
  }, [loadReferences]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const filteredRules = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    if (!normalizedKeyword) return rules;
    return rules.filter((rule) => {
      const target = [
        rule.ruleName,
        rule.ruleSource,
        rule.mappingStatus,
        rule.resultSoundType,
        rule.resultSoundSubType,
        rule.label?.labelCode,
        rule.label?.modelLabel,
        rule.label?.modelLabelKo,
        rule.memo,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return target.includes(normalizedKeyword);
    });
  }, [keyword, rules]);

  const filteredLabels = useMemo(() => {
    const normalizedKeyword = labelKeyword.trim().toLowerCase();
    return labels.filter((label) => {
      if (labelUseYn !== "ALL" && label.useYn !== labelUseYn) return false;
      if (!normalizedKeyword) return true;
      return [label.modelName, label.modelVersion, label.labelCode, label.modelLabel, label.modelLabelKo, label.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedKeyword);
    });
  }, [labelKeyword, labelUseYn, labels]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeStatus, keyword]);

  useEffect(() => {
    setLabelPage(1);
  }, [labelKeyword, labelUseYn]);

  const totalPages = Math.max(1, Math.ceil(filteredRules.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageRules = filteredRules.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const labelTotalPages = Math.max(1, Math.ceil(filteredLabels.length / LABEL_PAGE_SIZE));
  const safeLabelPage = Math.min(labelPage, labelTotalPages);
  const pageLabels = filteredLabels.slice((safeLabelPage - 1) * LABEL_PAGE_SIZE, safeLabelPage * LABEL_PAGE_SIZE);

  const metrics = useMemo(
    () => ({
      total: rules.length,
      applied: rules.filter((rule) => rule.mappingStatus === "APPLIED").length,
      candidate: rules.filter((rule) => rule.mappingStatus === "CANDIDATE").length,
      conflict: rules.filter((rule) => rule.conflictYn === "Y" || rule.mappingStatus === "CONFLICT").length,
    }),
    [rules],
  );

  return (
    <AdminPageLayout
      title="과거 YAMNet 매핑"
      subtitle="v1 YAMNet 원천 라벨의 운영 분류·학습 태그 이력을 조회합니다. 최신 EAFR 표시 정책과 구분합니다."
      actions={
        <AdminButton variant="primary" size="sm" icon={loading ? "ri-refresh-line animate-spin" : "ri-refresh-line"} onClick={loadRules} disabled={loading}>
          새로고침
        </AdminButton>
      }
    >
      <div className="min-w-0">
      <div className="mb-4">
        <AdminErrorBanner message={errorMessage} />
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
        <AdminMetricCard title="전체 룰" value={metrics.total} icon="ri-links-line" tone="blue" />
        <AdminMetricCard title="적용 중" value={metrics.applied} icon="ri-check-line" tone="emerald" />
        <AdminMetricCard title="후보" value={metrics.candidate} icon="ri-draft-line" tone="amber" />
        <AdminMetricCard title="충돌" value={metrics.conflict} icon="ri-error-warning-line" tone="rose" />
      </div>

      <AdminCard className="mb-4 min-w-0 overflow-hidden">
        <AdminCardHeader title="모델 라벨 현황" />
        <div className="flex flex-col gap-2 border-b border-slate-100 p-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1">
            {([['ALL', '전체'], ['Y', '사용'], ['N', '미사용']] as const).map(([value, label]) => (
              <AdminButton key={value} variant={labelUseYn === value ? "soft" : "ghost"} size="sm" onClick={() => setLabelUseYn(value)}>{label}</AdminButton>
            ))}
          </div>
          <AdminSearchInput className="w-full md:w-72" value={labelKeyword} onChange={setLabelKeyword} placeholder="모델, 버전, 라벨 검색" />
        </div>
        <AdminTable headers={["Index", "라벨 코드", "모델 라벨", "한국어 표시", "모델 / 버전", "상태"]} loading={loading} emptyMessage={!loading && filteredLabels.length === 0 ? "조건에 맞는 모델 라벨이 없습니다." : undefined}>
          {pageLabels.map((label) => (
            <AdminTr key={label.soundModelLabelId}>
              <AdminTd><span className="text-xs font-semibold text-slate-600">{label.labelIndex}</span></AdminTd>
              <AdminTd><span className="text-xs font-semibold text-slate-800">{label.labelCode}</span></AdminTd>
              <AdminTd><span className="text-xs text-slate-700">{label.modelLabel}</span></AdminTd>
              <AdminTd><span className="text-xs text-slate-600">{label.modelLabelKo ?? "-"}</span></AdminTd>
              <AdminTd><span className="text-xs text-slate-600">{label.modelName} / {label.modelVersion}</span></AdminTd>
              <AdminTd><AdminStatusBadge code={label.useYn} label={label.useYn === "Y" ? "사용" : "미사용"} tone={label.useYn === "Y" ? "success" : "neutral"} /></AdminTd>
            </AdminTr>
          ))}
        </AdminTable>
        <AdminPagination currentPage={safeLabelPage} totalPages={labelTotalPages} totalCount={filteredLabels.length} pageSize={LABEL_PAGE_SIZE} itemLabel="개" onPageChange={setLabelPage} />
      </AdminCard>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_26.25rem]">
        <AdminCard className="min-w-0 overflow-hidden">
          <AdminCardHeader title="매핑 룰 목록" />
          <div className="flex flex-col gap-2 border-b border-slate-100 p-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1">
              {MAPPING_STATUS_OPTIONS.map((option) => (
                <AdminButton variant="plain" size="auto"
                  key={option.value}
                  type="button"
                  onClick={() => setActiveStatus(option.value)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    activeStatus === option.value ? "bg-blue-50 text-blue-600" : "text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  {option.label}
                </AdminButton>
              ))}
            </div>
            <AdminSearchInput value={keyword} onChange={setKeyword} placeholder="라벨, 분류, 규칙명 검색" className="w-full md:w-72" />
          </div>
          <AdminTable
            headers={["YAMNet 라벨", "표시 / 분류", "정책 / 조건", "상태", "우선순위", "수정일"]}
            loading={loading}
            emptyMessage={!loading && filteredRules.length === 0 ? "등록된 소리 매핑 룰이 없습니다." : undefined}
          >
            {pageRules.map((rule) => (
              <AdminTr key={rule.soundModelMappingRuleId}>
                <AdminTd>
                  <div className="text-sm font-semibold text-slate-800">{labelText(rule.label)}</div>
                  <div className="mt-1 text-xs text-slate-400">
                    {rule.label?.modelName ?? "패턴"} / {rule.label?.modelVersion ?? rule.modelLabelPattern ?? "-"}
                  </div>
                </AdminTd>
                <AdminTd>
                  <div className="text-sm font-semibold text-slate-700">{rule.displayLabelKo ?? rule.resultSoundSubType}</div>
                  <div className="mt-1 text-xs text-slate-400">{rule.soundGroupNameKo ?? rule.soundGroupCode ?? rule.resultSoundType}</div>
                  {rule.riskLevel && <div className="mt-1 text-xs text-rose-500">위험 {rule.riskLevel} · 대상 {rule.riskFlagEligibleYn}</div>}
                </AdminTd>
                <AdminTd>
                  <div className="text-xs font-medium text-slate-600">{rule.thresholdPolicyVersion}</div>
                  <div className="mt-1 text-xs text-slate-400">{rule.thresholdPolicyStatusCode} · {rule.policyVersion ?? "정책 버전 없음"}</div>
                  <div className="text-xs text-slate-600">min {rule.minModelConfidence ?? "-"}</div>
                  <div className="mt-1 text-xs text-slate-400">max {rule.maxModelConfidence ?? "-"}</div>
                </AdminTd>
                <AdminTd>
                  <div className="flex flex-col items-start gap-1">
                    <AdminStatusBadge code={rule.mappingStatus} label={statusLabel(rule.mappingStatus)} tone={statusTone(rule.mappingStatus)} />
                    {rule.conflictYn === "Y" && <AdminStatusBadge code="CONFLICT" label="충돌 있음" tone="warning" />}
                    {rule.useYn !== "Y" && <AdminStatusBadge code="N" label="미사용" tone="neutral" />}
                    {!rule.mutationAllowed && <AdminStatusBadge code="READ_ONLY" label="검증 정책 · 조회 전용" tone="info" />}
                  </div>
                </AdminTd>
                <AdminTd>
                  <span className="text-sm text-slate-700">{rule.priority}</span>
                </AdminTd>
                <AdminTd>
                  <span className="text-xs text-slate-500">{formatDateTime(rule.updatedAt ?? rule.registeredAt)}</span>
                </AdminTd>
              </AdminTr>
            ))}
          </AdminTable>
          <AdminPagination
            currentPage={safePage}
            totalPages={totalPages}
            totalCount={filteredRules.length}
            pageSize={PAGE_SIZE}
            itemLabel="개"
            onPageChange={setCurrentPage}
          />
        </AdminCard>

      </div>
      </div>
    </AdminPageLayout>
  );
};

export default SoundModelMappingRulesPage;
